/**
 * Shared chip/badge tone vocabulary — Phase 7 of the frontend redesign
 * (docs/Frontend-Redesign-Plan.md). Before this file, at least 9 pages each invented
 * their own status/priority/tone-mapping, none importing or reusing each other, with
 * real disagreements between them (e.g. Medium mapped to 3 different tones depending
 * on the page). This is the one shared source of truth going forward.
 *
 * Consumers render a tone as: <span className={`np-chip np-tone-${tone}`}>{label}</span>
 */

export type Tone = 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'primary' | 'dark';

/** Look up a tone for a value against a fixed-enum table, falling back when the value
 * is missing/unrecognised (e.g. legacy data with a since-removed enum member). */
export function toneFromMap<T extends string>(
  map: Record<T, Tone>,
  value: T | string | null | undefined,
  fallback: Tone = 'neutral',
): Tone {
  if (!value) return fallback;
  return (map as Record<string, Tone>)[value] ?? fallback;
}

/**
 * The one fixed 4-level severity/criticality/priority scale used across risk-adjacent
 * registers (Critical/High/Medium/Low). Critical/High/Medium already matched between
 * AssetsPage's and SecurityObjectivesPage's independently-written tone tables before
 * this file existed — real converged-on precedent, not invented here. Low moves from
 * the `neutral` both of those used to `success`, matching RegisterItemList's
 * priorityTone() and the severity-gradient reasoning in docs/AI-Governance-Port-Plan.md
 * (very-low/low → success) — keeps all 4 levels visually distinct instead of Low
 * reading the same as an unrelated "no data" neutral state elsewhere in the same table.
 */
export const SEVERITY_TONE: Record<'Critical' | 'High' | 'Medium' | 'Low', Tone> = {
  Critical: 'danger',
  High: 'warning',
  Medium: 'info',
  Low: 'success',
};

/**
 * Data-sensitivity classification, moved here verbatim from constants/aiGovernance.ts
 * (which re-exports both of these so its existing 5 importers see no diff). Originally
 * ported from the FAIR app's SENSITIVITY_BADGE map — see docs/AI-Governance-Port-
 * Plan.md line 137. Also the correct tone/label source for AssetsPage's Classification
 * column (public/internal/confidential/restricted), which stores the same enum.
 */
export const SENSITIVITY_LABELS: Record<'public' | 'internal' | 'confidential' | 'restricted', string> = {
  public: 'Public',
  internal: 'Internal',
  confidential: 'Confidential',
  restricted: 'Restricted',
};

export const SENSITIVITY_TONE: Record<'public' | 'internal' | 'confidential' | 'restricted', Tone> = {
  public: 'success',
  internal: 'info',
  confidential: 'warning',
  restricted: 'danger',
};

/**
 * Extracted verbatim from RegisterItemList.tsx (the shared, config-driven register-list
 * engine) - genuinely a different problem from SEVERITY_TONE above, so kept separate
 * rather than folded in: a register built on this component has a tenant-defined,
 * free-text status/priority string, not a fixed enum, so keyword-matching is the correct
 * approach here, not a smell to converge away. 9 pages depend on this file's behaviour.
 */
export const fuzzyStatusTone = (status: string): Tone => {
  const s = status.toLowerCase();
  // Checked ahead of the 'active' bucket below: 'inactive' contains 'active' as a
  // substring, so without this an inactive asset/item would be misread as active and
  // shown as a reassuring success-green chip instead of a neutral one. Found while
  // wiring AssetsPage's Status column onto this function - its status enum is exactly
  // active/inactive/retired/maintenance.
  if (s.includes('inactive')) return 'neutral';
  if (s.includes('open') || s.includes('active') || s.includes('new')) return 'success';
  if (s.includes('closed') || s.includes('resolved') || s.includes('archived') || s.includes('done')) return 'neutral';
  if (s.includes('in progress') || s.includes('in_progress') || s.includes('ongoing')) return 'info';
  if (s.includes('overdue') || s.includes('critical') || s.includes('breach')) return 'danger';
  // A fresh draft isn't at risk of anything going wrong - it's just not started yet, a
  // different thing from an item genuinely pending/in-review. Matches
  // constants/aiGovernance.ts's already-shipped AI_STATUS_TONE.draft = 'neutral', and
  // resolves the one place that file and this one previously disagreed (this used to
  // bucket 'draft' in with 'pending'/'review' below, as 'warning').
  if (s.includes('draft')) return 'neutral';
  if (s.includes('pending') || s.includes('review')) return 'warning';
  return 'info';
};

export const fuzzyPriorityTone = (priority: string): Tone => {
  const p = priority.toLowerCase();
  if (p.includes('critical')) return 'danger';
  if (p.includes('high')) return 'danger';
  if (p.includes('medium') || p.includes('moderate')) return 'warning';
  if (p.includes('low')) return 'success';
  return 'neutral';
};
